import dayjs from 'dayjs';

export const SIGN_LINK_POLL_MS = 4000;

export const SIGN_LINK_STATUS_EXPIRED = -2;
export const SIGN_LINK_STATUS_REJECTED = -1;
export const SIGN_LINK_STATUS_PENDING = 0;
export const SIGN_LINK_STATUS_RELEASED = 1;
export const SIGN_LINK_STATUS_SIGNED = 2;

export const SIGN_LINK_SIGNER = 1;
export const SIGN_LINK_VISA = 2;

export type SignLinkBootstrap = {
  signerFirstName: string | null;
  signerLastName: string | null;
  documentTitle: string | null;
  operationId: string | null;
  validTo: string | null;
  status: number;
  signerType: number;
  signedAt: string | null;
  canSign: boolean;
  blockReason: string | null;
};

export type SignLinkStatusSnapshot = {
  status: number;
  signerType: number | null;
  signedAt: string | null;
  canSign: boolean;
  blockReason: string | null;
};

export type SimaQrResponse = {
  qrText: string;
  isDocumentLocked: boolean;
  lockReason: string | null;
};

export type SignLinkView = 'ready' | 'signing' | 'completed' | 'blocked';

export class SignLinkHttpError extends Error {
  readonly status: number;

  constructor(status: number) {
    super('Sign link request failed');
    this.name = 'SignLinkHttpError';
    this.status = status;
  }
}

const SAFE_SIMA_HREF = /^(?:sima:|https?:)/i;

function readRecord(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  return data as Record<string, unknown>;
}

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function booleanOrNull(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === 'true') return true;
  if (value === 0 || value === 'false') return false;
  return null;
}

export function isInviteSignPath(pathname: string): boolean {
  return pathname === '/s' || pathname.startsWith('/s/');
}

/** Drop the invite token from analytics URLs. Absolute URLs only. */
export function redactInviteSignUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (!isInviteSignPath(parsed.pathname)) return url;
    parsed.pathname = '/s';
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch {
    return url;
  }
}

export function signLinkApiUrl(
  token: string,
  action?: 'status' | 'simaqr',
  baseUrl: string | undefined = process.env.REACT_APP_SIGNER_API_URL,
): string {
  const base = (baseUrl ?? '').replace(/\/$/, '');
  const encoded = encodeURIComponent(token);
  if (action === 'status') return `${base}/api/sign-link/${encoded}/status`;
  if (action === 'simaqr') return `${base}/api/sign-link/${encoded}/simaqr?providerName=Sima`;
  return `${base}/api/sign-link/${encoded}`;
}

export function formatSignerName(firstName: string | null, lastName: string | null): string | null {
  const name = [firstName, lastName].filter((part): part is string => Boolean(part)).join(' ').trim();
  return name.length > 0 ? name : null;
}

export function formatSignLinkDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return null;
  return parsed.format('DD.MM.YYYY');
}

export function formatSignLinkDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return null;
  return parsed.format('DD.MM.YYYY HH:mm');
}

export function signLinkStatusKey(status: number): string {
  switch (status) {
    case SIGN_LINK_STATUS_EXPIRED:
      return 'signingPartyStatusExpired';
    case SIGN_LINK_STATUS_REJECTED:
      return 'signingPartyStatusRejected';
    case SIGN_LINK_STATUS_PENDING:
      return 'signLinkStatusPending';
    case SIGN_LINK_STATUS_RELEASED:
      return 'signLinkStatusReleased';
    case SIGN_LINK_STATUS_SIGNED:
      return 'signed';
    default:
      return 'signLinkStatusUnknown';
  }
}

const KNOWN_BLOCK_REASONS: Record<string, string> = {
  'this signing link has already been used': 'signLinkAlreadyUsed',
};

/** Map known English API reasons to a translation key. Unknown text is shown as returned. */
export function signLinkBlockReasonKey(reason: string): string | null {
  const normalized = reason.trim().replace(/[.!?]+$/u, '').trim().toLowerCase();
  return KNOWN_BLOCK_REASONS[normalized] ?? null;
}

export function signLinkActionKey(signerType: number): 'signLinkSignCta' | 'signLinkVisaCta' {
  return signerType === SIGN_LINK_VISA ? 'signLinkVisaCta' : 'signLinkSignCta';
}

export function getSignLinkView(
  link: Pick<SignLinkBootstrap, 'status' | 'canSign'>,
  signing: boolean,
): SignLinkView {
  if (link.status === SIGN_LINK_STATUS_SIGNED || link.status === SIGN_LINK_STATUS_RELEASED) {
    return 'completed';
  }
  if (
    link.status === SIGN_LINK_STATUS_EXPIRED ||
    link.status === SIGN_LINK_STATUS_REJECTED ||
    link.canSign === false
  ) {
    return 'blocked';
  }
  if (signing) return 'signing';
  return 'ready';
}

export function safeSimaHref(qrText: string): string | null {
  const trimmed = qrText.trim();
  if (!trimmed || !SAFE_SIMA_HREF.test(trimmed)) return null;
  return trimmed;
}

export function parseSignLinkBootstrap(data: unknown): SignLinkBootstrap | null {
  const record = readRecord(data);
  if (!record) return null;

  const status = numberOrNull(record.status ?? record.Status);
  const canSign = booleanOrNull(record.canSign ?? record.CanSign);
  const signerType = numberOrNull(record.signerType ?? record.SignerType);
  if (status == null || canSign == null) return null;

  return {
    signerFirstName: stringOrNull(record.signerFirstName ?? record.SignerFirstName),
    signerLastName: stringOrNull(record.signerLastName ?? record.SignerLastName),
    documentTitle: stringOrNull(record.documentTitle ?? record.DocumentTitle),
    operationId: stringOrNull(record.operationId ?? record.OperationId),
    validTo: stringOrNull(record.validTo ?? record.ValidTo),
    status,
    signerType: signerType ?? SIGN_LINK_SIGNER,
    signedAt: stringOrNull(record.signedAt ?? record.SignedAt),
    canSign,
    blockReason: stringOrNull(record.blockReason ?? record.BlockReason),
  };
}

export function parseSignLinkStatus(data: unknown): SignLinkStatusSnapshot | null {
  const record = readRecord(data);
  if (!record) return null;

  const status = numberOrNull(record.status ?? record.Status);
  const canSign = booleanOrNull(record.canSign ?? record.CanSign);
  if (status == null || canSign == null) return null;

  return {
    status,
    signerType: numberOrNull(record.signerType ?? record.SignerType),
    signedAt: stringOrNull(record.signedAt ?? record.SignedAt),
    canSign,
    blockReason: stringOrNull(record.blockReason ?? record.BlockReason),
  };
}

export function mergeSignLinkStatus(
  current: SignLinkBootstrap,
  next: SignLinkStatusSnapshot,
): SignLinkBootstrap {
  return {
    ...current,
    status: next.status,
    signerType: next.signerType ?? current.signerType,
    signedAt: next.signedAt,
    canSign: next.canSign,
    blockReason: next.blockReason,
  };
}

export function parseSimaQrResponse(data: unknown): SimaQrResponse | null {
  const record = readRecord(data);
  if (!record) return null;
  const locked = booleanOrNull(record.isDocumentLocked ?? record.IsDocumentLocked);
  return {
    qrText: stringOrNull(record.qrText ?? record.QrText) ?? '',
    isDocumentLocked: locked === true,
    lockReason: stringOrNull(record.lockReason ?? record.LockReason),
  };
}

async function readSignLinkResponse(response: Response): Promise<unknown> {
  if (!response.ok) {
    throw new SignLinkHttpError(response.status);
  }
  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new SignLinkHttpError(response.status);
  }
}

async function requestSignLink(url: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(url, {
    cache: 'no-store',
    ...init,
    headers: {
      Accept: 'application/json',
      ...init?.headers,
    },
  });
  return readSignLinkResponse(response);
}

export async function fetchSignLink(token: string): Promise<SignLinkBootstrap> {
  const data = await requestSignLink(signLinkApiUrl(token));
  const parsed = parseSignLinkBootstrap(data);
  if (!parsed) throw new SignLinkHttpError(502);
  return parsed;
}

export async function fetchSignLinkStatus(token: string): Promise<SignLinkStatusSnapshot> {
  const data = await requestSignLink(signLinkApiUrl(token, 'status'));
  const parsed = parseSignLinkStatus(data);
  if (!parsed) throw new SignLinkHttpError(502);
  return parsed;
}

export async function startSimaSign(token: string): Promise<SimaQrResponse> {
  const data = await requestSignLink(signLinkApiUrl(token, 'simaqr'), { method: 'POST' });
  const parsed = parseSimaQrResponse(data);
  if (!parsed) throw new SignLinkHttpError(502);
  return parsed;
}
